import { apiUpdateSensor } from 'apis/SensorApi';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { SENSOR_STATUS_ALERT } from 'constants/config';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useSensors } from 'context/SensorsContext';
import { LockState } from 'models';
import PropTypes from 'prop-types';
import React from 'react';
import Popup from '../../components/Popup';
import "./MapPage.css";
import MapView from './components/MapView';
import { PopupContent } from './components/SensorInfoCard';
import SensorSearchbar from './components/SensorSearchbar';
import { SensorType } from 'models';

const propTypes = {
  isTop: PropTypes.bool,
  onMapLoad: PropTypes.func,
  onViewSensorDetail: PropTypes.func,
  onSendMail: PropTypes.func,
};
const defaultProps = {
  isTop: true,
  onMapLoad: (map) => { },
  onViewSensorDetail: (sensor) => { },
  onSendMail: (sensor) => { },
};
const MapPage = (props) => {
  /*** States and Variables ***/
  const mapRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const geoJsonRef = React.useRef(null);
  const mouseEventRef = React.useRef(null);
  const [selectedSensorId, setSelectedSensorId] = React.useState('');
  const { generalSetting } = useGeneralSetting();
  const [loading, setLoading] = React.useState(false);
  const { error } = useDialog();
  const [isOpen, setIsOpen] = React.useState(false);
  const [sensorInfo, setSensorInfo] = React.useState(null);
  const [hasWarningSensor, setHasWarningSensor] = React.useState(false);
  const { sensors, layers, analyze } = useSensors();
  const { sensorGroups } = useSensorGroups();
  const [zoom, setZoom] = React.useState(5); // list of current sensors shown on map
  const [isMouseOutsidePopup, setIsMouseOutsidePopup] = React.useState(false);

  /*** Processing ***/
  React.useEffect(() => {
    if (mapRef.current && generalSetting?.lat && generalSetting?.lng) {
      mapRef.current.setView(
        {
          lat: generalSetting.lat,
          lon: generalSetting.lng
        },
        generalSetting.zoom);
    } else if (geoJsonRef.current && generalSetting?.boundingBox) {
      mapRef.current.fitBounds(geoJsonRef.current.getBounds())
    }
  }, [generalSetting])

  React.useEffect(() => {
    if (sensorInfo?.length) {
      const ids = sensorInfo.map((item) => item.id);
      const newSensor = sensors.filter((item) => ids.includes(item.id));
      if (newSensor) setSensorInfo(newSensor);
    }
    const warningStatus = sensors.filter((sensor) => {
      const targetGroup = sensorGroups.find(g => g.id === sensor.groupId);
      if (targetGroup?.messages[sensor.status] && targetGroup.messages[sensor.status].value === SENSOR_STATUS_ALERT) return true;
      if (generalSetting?.alertOn && sensor.isLocked) return true;
      return false;
    })
    setHasWarningSensor(!!warningStatus.length);
  }, [sensors, sensorGroups, generalSetting]);

  React.useEffect(() => {
    const handleMouseMove = (event) => {
      if (popupRef.current) {
        const rect = popupRef.current.getBoundingClientRect();
        const { clientX, clientY } = event;

        // Kiểm tra nếu con trỏ chuột nằm ngoài popup
        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          setIsMouseOutsidePopup(true);
        } else {
          setIsMouseOutsidePopup(false);
        }
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      setSelectedSensorId('');
      setSensorInfo(null);
    };
  }, []);

  React.useEffect(() => {
    if (!props.isTop) {
      setIsOpen(false);
      setSensorInfo(null);
      setSelectedSensorId('');
      setIsMouseOutsidePopup(false);
    }
  }, [props.isTop])

  /*** Sub Components ***/
  /*** Event Handlers ***/

  const calculateOffsetLng = (lng, lat, newZoom) => {
    const centerPoint = mapRef.current.latLngToContainerPoint([lat, lng]);
    const scaleFactor = Math.pow(2, newZoom - mapRef.current.getZoom());
    const offsetX = (window.innerWidth / 4) / scaleFactor;

    const targetPoint = { x: centerPoint.x + offsetX, y: centerPoint.y };
    const newLatLng = mapRef.current.containerPointToLatLng(targetPoint);
    return newLatLng.lng - lng;
  };

  const onViewportChange = React.useCallback((e) => {
    const map = e.target;
    setZoom(map.getZoom());
  }, [layers])

  const onMarkerClick = React.useCallback(async (sensor) => {
    setIsOpen(true);
    setSelectedSensorId(sensor.id);
    mouseEventRef.current = 'click';
    if (sensor.id.includes('#')) {
      const ids = analyze(sensor.id);
      const group = sensors.filter(s => ids.includes(s.id));
      setSensorInfo(group);
    } else {
      setSensorInfo([sensor]);
    }
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      const newZoom = Math.min(currentZoom + 3, 18);
      const zoomOnClick = generalSetting?.zoomOnClick;
      const offsetLng = sensor.data === "DOUBLE" ? calculateOffsetLng(sensor.lng, sensor.lat, zoomOnClick ? newZoom : currentZoom) : 0;

      mapRef.current.flyTo({ lat: sensor.lat, lon: sensor.lng + offsetLng }, zoomOnClick ? newZoom : undefined);
    }
  }, [sensors, generalSetting])

  const onMarkerMouseOver = React.useCallback((sensor) => {
    // const targetGroup = sensorGroups.find(g => g.id === sensor.groupId || sensor.groupId.includes(g.id));
    // if (targetGroup.type === SensorType.CAMERA) {
    setIsOpen(true);
    setSelectedSensorId(prevSelectedSensorId => {
      if ((!mouseEventRef.current || mouseEventRef.current === 'click') && sensor.id !== prevSelectedSensorId) {
        mouseEventRef.current = 'mouseover';
      }
      return sensor.id; // Cập nhật state một cách an toàn
    });
    if (sensor.id.includes('#')) {
      const ids = analyze(sensor.id);
      const group = sensors.filter(s => ids.includes(s.id));
      setSensorInfo(group);
    } else {
      setSensorInfo([sensor]);
    }
    // }
  }, [sensors, sensorGroups, analyze])

  const onMarkerMouseOut = React.useCallback((sensor) => {
    // const targetGroup = sensorGroups.find(g => g.id === sensor.groupId || sensor.groupId.includes(g.id));
    // if (targetGroup.type === SensorType.CAMERA && mouseEventRef.current === "mouseover" && isMouseOutsidePopup) {
    if (mouseEventRef.current === "mouseover" && isMouseOutsidePopup) {
      setIsOpen(false);
      setSensorInfo(null);
    }
  }, [isMouseOutsidePopup])
  // }, [sensorGroups, isMouseOutsidePopup])

  const onPopupMouseLeave = () => {
    if (mouseEventRef.current === 'mouseover') {
      setIsOpen(false);
      setSensorInfo(null);
    }
  }

  const onPopupClose = () => {
    setIsOpen(false);
    setSensorInfo(null);
    setIsMouseOutsidePopup(false);
    setSelectedSensorId('');
    mouseEventRef.current = null;
  }

  const onMapClick = React.useCallback(() => {
    setSelectedSensorId('');
  }, [])

  const onMapLoad = React.useCallback(async (e) => {
    const map = e.target;
    mapRef.current = map;
    props.onMapLoad(map);
    setZoom(map.getZoom());
  }, [props.onMapLoad])

  const onSensorClick = async (sensor) => {
    mapRef.current.flyTo({ lat: sensor.lat, lon: sensor.lng }, mapRef.current.getZoom());
  }

  const onUnlockSensor = async (sensor) => {
    if (loading) return;
    setLoading(true);
    const result = await apiUpdateSensor({
      id: sensor.id,
      isLocked: LockState.UNLOCKED
    });
    setLoading(false);
    if (!result.success) {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
    }
  }

  /*** Main Render ***/
  return <div className='map_page_container'>
    <MapView
      markers={layers[Math.floor(zoom + 3)]}
      geoData={generalSetting?.boundingBox}
      geoJsonRef={geoJsonRef}
      selectedId={selectedSensorId}
      onMarkerClick={onMarkerClick}
      onMarkerMouseOver={onMarkerMouseOver}
      onMarkerMouseOut={onMarkerMouseOut}
      onViewportChange={onViewportChange}
      onMapClick={onMapClick}
      onMapLoad={onMapLoad} />
    {
      <>
        <SensorSearchbar
          onClick={onSensorClick}
          onUnlock={onUnlockSensor}
          setSensorInfo={setSensorInfo}
          sensorInfo={sensorInfo}
          onViewSensorDetail={props.onViewSensorDetail}
          onSendMail={props.onSendMail}
          hasWarningSensor={hasWarningSensor}
        />
        <Popup
          ref={popupRef}
          isOpen={isOpen}
          isDrag={false}
        >
          <PopupContent
            setIsOpen={setIsOpen}
            sensors={sensorInfo}
            onUnlockSensor={onUnlockSensor}
            onClose={onPopupClose}
            onPopupMouseLeave={onPopupMouseLeave}
            onViewSensorDetail={(sensor, state) => { props.onViewSensorDetail(sensors.find(s => s.id === sensor.id), state) }}
            onSendMail={props.onSendMail}
          />
        </Popup>
      </>
    }

    {loading && <FanLoadingIcon size={80} />}
  </div>;
}

MapPage.propTypes = propTypes;
MapPage.defaultProps = defaultProps;
export default MapPage;